import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import api from '../api';

const fallback = [
  { name: 'Dubai',          slug: 'dubai',        country: 'UAE' },
  { name: 'Turkey',         slug: 'turkey',       country: 'Turkey' },
  { name: 'Morocco',        slug: 'morocco',      country: 'Morocco' },
  { name: 'Canada',         slug: 'canada',       country: 'Canada' },
  { name: 'Europe',         slug: 'europe',       country: 'Europe' },
  { name: 'China',          slug: 'china',        country: 'China' },
  { name: "Côte d'Ivoire",  slug: 'cote-divoire', country: "Côte d'Ivoire" },
  { name: 'Thailand',       slug: 'thailand',     country: 'Thailand' },
  { name: 'Bali',           slug: 'bali',         country: 'Indonesia' }
];

function DestinationCard({ d }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={'/destinations/' + d.slug}
      style={{ textDecoration: 'none', display: 'block' }}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
    >
      <div style={{
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: hovered
          ? '0 20px 48px rgba(17,45,78,0.22)'
          : '0 6px 24px rgba(17,45,78,0.10)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'transform 0.35s ease, box-shadow 0.35s ease',
        aspectRatio: '4 / 3'
      }}>
        {/* Full bleed image */}
        <img
          src={d.heroImage || 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&q=80'}
          alt={d.name}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            transform: hovered ? 'scale(1.07)' : 'scale(1)',
            transition: 'transform 0.5s ease'
          }}
        />

        {/* Gradient overlay — always present, deepens on hover */}
        <div style={{
          position: 'absolute', inset: 0,
          background: hovered
            ? 'linear-gradient(to top, rgba(17,45,78,0.88) 0%, rgba(17,45,78,0.4) 50%, rgba(17,45,78,0.05) 100%)'
            : 'linear-gradient(to top, rgba(17,45,78,0.72) 0%, rgba(17,45,78,0.15) 60%, transparent 100%)',
          transition: 'background 0.35s ease'
        }} />

        {/* Country pill — top left */}
        <div style={{
          position: 'absolute', top: 14, left: 14,
          background: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 20, padding: '4px 12px',
          fontSize: '0.72rem', fontWeight: 700,
          color: '#fff', letterSpacing: '0.06em',
          textTransform: 'uppercase'
        }}>
          {d.country}
        </div>

        {/* Explore badge — top right, shows on hover */}
        <div style={{
          position: 'absolute', top: 14, right: 14,
          background: 'var(--bj-clay)',
          borderRadius: 20, padding: '4px 12px',
          fontSize: '0.72rem', fontWeight: 700,
          color: '#fff',
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(-6px)',
          transition: 'opacity 0.3s ease, transform 0.3s ease'
        }}>
          Explore →
        </div>

        {/* Bottom info — always visible */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '20px 18px 18px'
        }}>
          <h3 style={{
            margin: '0 0 4px', color: '#fff',
            fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
            fontFamily: 'var(--font-display)',
            textShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}>
            {d.name}
          </h3>

          {/* Description preview — slides up on hover */}
          {d.overview && (
            <p style={{
              margin: 0, fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.5,
              maxHeight: hovered ? 60 : 0,
              overflow: 'hidden',
              opacity: hovered ? 1 : 0,
              transition: 'max-height 0.35s ease, opacity 0.35s ease'
            }}>
              {d.overview.slice(0, 90)}...
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Destinations() {
  const [destinations, setDestinations] = useState(fallback);
  const [loading, setLoading]           = useState(true);

  useEffect(function() {
    api.get('/destinations')
      .then(function(res) { if (res.data.length) setDestinations(res.data); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  return (
    <Layout>
      {/* HERO BAND */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bj-espresso) 0%, #1e3f6b 100%)',
        padding: '64px 0 56px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(63,114,175,0.1)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>
          <span className="eyebrow" style={{ color: 'var(--bj-sand)' }}>Explore</span>
          <h1 style={{ color: '#fff', margin: '10px 0 14px', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
            Destinations
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 520, lineHeight: 1.75, margin: 0 }}>
            Dedicated guides for our most-requested destinations — curated by our travel specialists with local expertise and genuine insider knowledge.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading ? <Loader /> : (
            <div className="grid-3">
              {destinations.map(function(d) {
                return <DestinationCard key={d.slug} d={d} />;
              })}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}